import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tag, Upload, Trash2, FileSpreadsheet, ArrowRight, GitCompareArrows } from "lucide-react";

const fmtD = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const inputCls = "bg-zinc-950 border-zinc-800";

function ImportDialog({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [meta, setMeta] = useState({ name: "", date_from: "", date_to: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const reset = () => { setStep(1); setParsed(null); setMapping({}); setMeta({ name: "", date_from: "", date_to: "", notes: "" }); };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/promo-flyers/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data); setMapping(data.mapping || {}); setStep(2);
      if (!meta.name) setMeta((m) => ({ ...m, name: file.name.replace(/\.[^.]+$/, "") }));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    if (meta.name.trim().length < 2 || !meta.date_from || !meta.date_to) { toast.error("Inserisci nome e periodo di validità"); return; }
    if (mapping.code == null && mapping.description == null) { toast.error("Indica almeno la colonna codice o descrizione"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/promo-flyers", { ...meta, mapping, rows: parsed.rows });
      toast.success(`Volantino salvato: ${data.items.filter((i) => i.match !== "none").length}/${data.items.length} prodotti abbinati al CRM`);
      onCreated(data); reset();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const colSelect = (key, label) => (
    <div>
      <Label className="text-zinc-400 text-xs">{label}</Label>
      <select value={mapping[key] ?? ""} onChange={(e) => setMapping({ ...mapping, [key]: e.target.value === "" ? undefined : Number(e.target.value) })} data-testid={`flyer-map-${key}`} className="w-full h-9 rounded-md bg-zinc-950 border border-zinc-800 text-sm px-2">
        <option value="">— non presente —</option>
        {parsed.columns.map((c, i) => <option key={i} value={i}>{c}</option>)}
      </select>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); reset(); } }}>
      <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-3xl" data-testid="flyer-import-dialog">
        <DialogHeader><DialogTitle>Importa volantino {step === 2 && `— ${parsed.filename} (${parsed.total_rows} righe)`}</DialogTitle></DialogHeader>
        {step === 1 && (
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-700 rounded-xl p-10 cursor-pointer hover:border-[color:var(--paprika)]" data-testid="flyer-dropzone">
            <FileSpreadsheet className="h-10 w-10 text-zinc-500" />
            <span className="text-sm">{busy ? "Lettura file…" : "Clicca o trascina un file Excel (.xlsx) o CSV"}</span>
            <span className="text-[11px] text-zinc-500">Colonne attese: codice prodotto / EAN, descrizione, prezzo promo e (opzionale) prezzo pieno. Le intestazioni vengono riconosciute automaticamente.</span>
            <input type="file" accept=".xlsx,.xlsm,.xls,.csv,.txt" className="hidden" data-testid="flyer-file-input" onChange={(e) => upload(e.target.files?.[0])} disabled={busy} />
          </label>
        )}
        {step === 2 && parsed && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">{colSelect("code", "Colonna codice / EAN")}{colSelect("description", "Colonna descrizione")}{colSelect("price", "Colonna prezzo promo")}{colSelect("full_price", "Prezzo pieno (opz.)")}</div>
            <p className="text-[11px] text-zinc-500">Con il prezzo pieno vedrai sconto medio applicato e incasso perso per prodotto. Puoi anche inserirlo dopo, prodotto per prodotto.</p>
            <div className="overflow-auto max-h-48 rounded-lg border border-zinc-800">
              <table className="w-full text-[11px]" data-testid="flyer-preview-table">
                <thead className="bg-zinc-950 text-zinc-500 sticky top-0"><tr>{parsed.columns.map((c, i) => <th key={i} className={`p-1.5 text-left ${Object.values(mapping).includes(i) ? "text-[color:var(--paprika)]" : ""}`}>{c}</th>)}</tr></thead>
                <tbody>{parsed.rows.slice(0, 8).map((r, ri) => <tr key={ri} className="border-t border-zinc-800/60">{r.map((c, ci) => <td key={ci} className="p-1.5 truncate max-w-[200px]">{c}</td>)}</tr>)}</tbody>
              </table>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label className="text-zinc-400 text-xs">Nome volantino</Label><Input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} data-testid="flyer-name" className={inputCls} /></div>
              <div><Label className="text-zinc-400 text-xs">Valido dal</Label><Input type="date" value={meta.date_from} onChange={(e) => setMeta({ ...meta, date_from: e.target.value })} data-testid="flyer-from" className={inputCls} /></div>
              <div><Label className="text-zinc-400 text-xs">Al</Label><Input type="date" value={meta.date_to} onChange={(e) => setMeta({ ...meta, date_to: e.target.value })} data-testid="flyer-to" className={inputCls} /></div>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => { setStep(1); setParsed(null); }} data-testid="flyer-back">Cambia file</Button>
              <Button disabled={busy} onClick={save} data-testid="flyer-save" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">{busy ? "Abbinamento prodotti CRM…" : "Salva e abbina al CRM"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PromoFlyers() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/promo-flyers").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const del = async (f) => {
    if (!window.confirm(`Eliminare il volantino "${f.name}"?`)) return;
    try { await api.delete(`/promo-flyers/${f.id}`); toast.success("Volantino eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="promo-flyers">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Tag className="h-6 w-6 text-[color:var(--paprika)]" /> Analisi Volantini (CRM Elastic)</h1>
          <p className="text-sm text-zinc-500 mt-1">Importa l'elenco dei prodotti in promozione (Excel/CSV) e analizza come hanno venduto nel periodo del volantino, per prodotto e per punto vendita.</p>
        </div>
        <div className="flex items-center gap-2">
          {rows && rows.length >= 2 && <Link to={`/volantini-promo/confronto?a=${rows[0].id}&b=${rows[1].id}`} data-testid="flyer-compare-link"><Button variant="outline" className="border-zinc-800"><GitCompareArrows className="h-4 w-4 mr-1" /> Confronta volantini</Button></Link>}
          <Button onClick={() => setOpen(true)} data-testid="flyer-import-open" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"><Upload className="h-4 w-4 mr-1" /> Importa volantino</Button>
        </div>
      </div>
      <ImportDialog open={open} onClose={() => setOpen(false)} onCreated={(f) => { setOpen(false); nav(`/volantini-promo/${f.id}`); }} />
      {rows && rows.length === 0 && <div className="card-elev p-8 text-center text-sm text-zinc-500" data-testid="promo-flyers-empty">Nessun volantino importato. Clicca "Importa volantino" per iniziare.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="promo-flyers-list">
        {rows?.map((f) => (
          <div key={f.id} className="card-elev p-4 space-y-2" data-testid={`promo-flyer-${f.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div><div className="font-heading font-bold">{f.name}</div><div className="text-xs text-zinc-400">{fmtD(f.date_from)} → {fmtD(f.date_to)}</div></div>
              <Button size="icon" variant="ghost" onClick={() => del(f)} data-testid={`promo-flyer-delete-${f.id}`} className="h-7 w-7 text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="text-[11px] text-zinc-500">{f.items_count} prodotti · <span className="text-emerald-400">{f.matched} abbinati</span>{f.unmatched > 0 && <> · <span className="text-amber-300">{f.unmatched} da abbinare</span></>}</div>
            <Link to={`/volantini-promo/${f.id}`} className="inline-flex items-center gap-1 text-xs text-[color:var(--paprika)] hover:underline" data-testid={`promo-flyer-open-${f.id}`}>Apri analisi <ArrowRight className="h-3 w-3" /></Link>
          </div>
        ))}
      </div>
    </div>
  );
}
